import { Capacitor } from '@capacitor/core'
import {
  ActorProvider,
  AnalyticsProvider,
  AuthProvider,
  AuthSubjectProvider,
  LocalProvider,
  ProjectProvider,
  Server,
  useProjectContext,
} from '@healthblocks-io/core'
import { useEffect } from 'react'
import { Link, Navigate, Outlet, useParams } from 'react-router-dom'

import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { ProtectedAdminMessageRoute } from '@/modules/app/components/protected-admin-route/ProtectedAdminRoute'
import { ErrorPage } from '@/modules/app/pages/error/Error.page'
import { HealthblocksAppProvider } from '@/modules/healthblocks/providers/healthblocks-app-provider/HealthblocksAppProvider'
import { servers, tenantSlugs } from '@/modules/healthblocks/ServerProvider'
import { useTenantLocaleSync } from '@/modules/i18n/useTenantLocaleSync'
import { hospitalSessionStorage } from '@/modules/shared/helpers/hospital-session'
import { usePersistedTenant } from '@/modules/shared/hooks/usePersistedTenant'
import { UniversalLinkManager } from '../../deep-links/UniversalLinkManager'

import type { TenantSlug } from '@/modules/healthblocks/ServerProvider'

let activeTenant = 'azmm'
/**
 * TenantGuard validates that the tenant parameter is valid (azmm or olvw)
 * and ensures the current server matches the tenant from the URL.
 */
export const TenantGuard = () => {
  const { tenant } = useParams<{ tenant: TenantSlug }>()
  const { setPersistedTenant } = usePersistedTenant()
  useTenantLocaleSync(tenant!)

  useEffect(() => {
    if (tenant && tenantSlugs.includes(tenant)) {
      setPersistedTenant(tenant)
    }
  }, [tenant, setPersistedTenant])

  // Validate tenant parameter
  if (!tenantSlugs.includes(tenant!)) {
    if (!activeTenant) return <Navigate to={`/?redirect=${encodeURIComponent(window.location.pathname)}`} replace />
    const { pathname, search, hash } = window.location
    return <Navigate to={'/' + activeTenant + pathname + search + hash} replace />
  }

  const server = servers.find((s) => s.tenant === tenant)

  if (!server) {
    return <ErrorPage message={`Server "${tenant}" not found`} />
  }

  // Soft migration for navigate()
  // Keep track of which tenant this page was opened
  // so we can prefix URLs that are not yet tenant-aware
  if (tenant && tenant !== activeTenant) activeTenant = tenant

  return (
    <Server.Provider value={server}>
      <ProjectProvider
        pid={'' + server.projectId}
        fallback={() => {
          const project = useProjectContext()
          if (project.loading) return null
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: 16,
                gap: 16,
                flexDirection: 'column',
                minHeight: '90vh',
              }}
            >
              {project.error
                ? `Er liep iets mis bij het verbinden met ${server.display}: ${project.error.message}`
                : project.loading
                  ? `Verbinding maken met ${server.display}...`
                  : `Kon niet verbinden met ${server.display}.`}
              <div>
                <Button variant='Secondary' as={Link} to='/'>
                  Wisselen van ziekenhuis
                </Button>
              </div>
            </div>
          )
        }}
      >
        <LocalProvider scope='local'>
          <AuthProvider
            platform={Capacitor.getPlatform() as 'android' | 'ios' | 'web'}
            storage={hospitalSessionStorage()}
            minimumExpiresIn={20}
          >
            <ActorProvider include={['identifiers']}>
              <AuthSubjectProvider include={['identifiers']}>
                <AnalyticsProvider>
                  <UniversalLinkManager />
                  <HealthblocksAppProvider>
                    <ProtectedAdminMessageRoute>
                      <Outlet />
                    </ProtectedAdminMessageRoute>
                  </HealthblocksAppProvider>
                </AnalyticsProvider>
              </AuthSubjectProvider>
            </ActorProvider>
          </AuthProvider>
        </LocalProvider>
      </ProjectProvider>
    </Server.Provider>
  )
}
